import { useUiStore } from '@/store/ui';
import { useState, useEffect, useCallback } from 'react';

type GeolocationState = {
  coords: { lat: number; lng: number; accuracy: number } | null;
  permissionStatus: 'idle' | 'granted' | 'denied' | 'prompt' | 'loading';
  error: string | null;
};

export const useGeolocation = (enabled = false) => {
  const setIsGlobalLoading = useUiStore((s) => s.setIsGlobalLoading);
  const [state, setState] = useState<GeolocationState>({
    coords: null,
    permissionStatus: 'idle',
    error: null,
  });

  // Verifica el estado actual del permiso sin pedirlo
  const checkPermission = useCallback(async () => {
    if (!navigator.geolocation) {
      setState(prev => ({ ...prev, permissionStatus: 'denied', error: 'Tu navegador no soporta geolocalización.' }));
      return;
    }

    try {
      // API Permissions — soportada en Chrome, Firefox, Edge. Safari parcial
      const result = await navigator.permissions.query({ name: 'geolocation' });
      setState(prev => ({ ...prev, permissionStatus: result.state as any }));

      // Escucha cambios en tiempo real (usuario habilita/deshabilita)
      result.onchange = () => {
        setState(prev => ({ ...prev, permissionStatus: result.state as any, error: null }));
        if (result.state === 'granted') getCurrentCoords();
      };
    } catch {
      // Safari no soporta permissions.query — intenta directamente
      setState(prev => ({ ...prev, permissionStatus: 'prompt' }));
    }
  }, []);

  // Obtiene las coordenadas actuales
  const getCurrentCoords = useCallback((): Promise<{ lat: number; lng: number; accuracy: number }> => {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) return reject(new Error('Geolocalización no soportada'));

        setState(prev => ({ ...prev, permissionStatus: 'loading', error: null }));

        const ACCURACY_THRESHOLD = 30; // metros — ajusta según necesidad
        const MAX_WAIT_MS = 15000;     // máximo 15s esperando buena señal
        let watchId: number | null = null;

        const timeout = setTimeout(() => {
          if (watchId !== null) {
            navigator.geolocation.clearWatch(watchId);
          }
          // Si agotó el tiempo, usa la mejor lectura que haya obtenido
          if (bestReading) {
              setState({ coords: bestReading, permissionStatus: 'granted', error: null });
              resolve(bestReading);
              setIsGlobalLoading(false)
          } else {
              const msg = 'No se pudo obtener una ubicación precisa. Intenta en campo abierto.';
              setState(prev => ({ ...prev, permissionStatus: 'denied', error: msg }));
              reject(new Error(msg));
              setIsGlobalLoading(false)
          }
        }, MAX_WAIT_MS);

        let bestReading: { lat: number; lng: number; accuracy: number } | null = null;

        watchId = navigator.geolocation.watchPosition(
            (position) => {
                const reading = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    accuracy: position.coords.accuracy, // metros
                };

                // Guarda la mejor lectura obtenida hasta ahora
                if (!bestReading || reading.accuracy < bestReading.accuracy - 5) {
                  bestReading = reading;
                  // Actualiza el estado para mostrar progreso al usuario
                  setState({ coords: reading, permissionStatus: 'loading', error: null });
                }

                // Si ya es suficientemente precisa, resuelve sin esperar más
                if (reading.accuracy <= ACCURACY_THRESHOLD) {
                  clearTimeout(timeout);
                  if (watchId !== null) {
                    navigator.geolocation.clearWatch(watchId);}
                  setState({ coords: reading, permissionStatus: 'granted', error: null });
                  resolve(reading);
                  setIsGlobalLoading(false)
                }
            },
            (error) => {
                setIsGlobalLoading(false)
                clearTimeout(timeout);
                if (watchId !== null) {
                  navigator.geolocation.clearWatch(watchId);}
                const mensaje =
                error.code === 1 ? 'Permiso de ubicación denegado.'
                : error.code === 2 ? 'No se pudo obtener tu ubicación.'
                : 'Tiempo de espera agotado.';
                setState(prev => ({ ...prev, permissionStatus: 'denied', error: mensaje }));
                reject(new Error(mensaje));
            },
            {
                enableHighAccuracy: true, // fuerza uso de GPS en vez de WiFi/red
                timeout: MAX_WAIT_MS,
                maximumAge: 0,            // nunca usar caché
            }
        );
    });
    }, []);
//   const getCurrentCoords = useCallback((): Promise<{ lat: number; lng: number }> => {
//     return new Promise((resolve, reject) => {
//       setState(prev => ({ ...prev, permissionStatus: 'loading', error: null }));

//       navigator.geolocation.getCurrentPosition(
//         (position) => {
//           const coords = {
//             lat: position.coords.latitude,
//             lng: position.coords.longitude,
//           };
//           setState({ coords, permissionStatus: 'granted', error: null });
//           resolve(coords);
//         },
//         (error) => {
//           const mensaje =
//             error.code === 1 ? 'Permiso de ubicación denegado. Actívalo en la configuración de tu navegador.'
//             : error.code === 2 ? 'No se pudo obtener tu ubicación. Verifica tu conexión o GPS.'
//             : 'Tiempo de espera agotado al obtener ubicación.';

//           setState(prev => ({ ...prev, permissionStatus: 'denied', error: mensaje }));
//           reject(new Error(mensaje));
//         },
//         { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
//       );
//     });
//   }, []);

  useEffect(() => {
    if (!enabled) return;
    checkPermission();
  }, [enabled, checkPermission]);

  return { ...state, getCurrentCoords, checkPermission };
};